import { takeEvery, take, call, put, select, fork, cancel } from 'redux-saga/effects';
// FIX: Use require for throttle/takeLatest/buffers/Task/delay to bypass TS errors
const { throttle, takeLatest, buffers, delay } = require('redux-saga');
// takeEvery is usually available in the main export
import * as api from './api';
import { formValues, getPreload, getTextField, getUrl } from './selectors';
import { List } from 'immutable';
import { change } from 'redux-form/immutable';
import { SEARCH_THROUGH_DB, CREATE_AUTOCOMPLETE, OPTION_CHOSEN, DOES_NOT_EXIST } from './constants';
import {
  setDataSource,
  registerAutocomplete,
  setChosenText,
  setOrigin,
  ISearchThroughDatabaseAction,
  setPreload,
} from './actions';
export function* fetchPreload(name: string, textField: string, url: string, preload: any) {
  const entityName = name.split('[')[0];
  const valueName = name.split(']')[1].slice(1, name.length);
  const preloadName = `${entityName}${valueName ? `.${valueName}` : ''}`;
  const availablePreload = yield select(getPreload(preloadName));
  let initialDataSource;
  if (availablePreload) {
    initialDataSource = availablePreload.toJS();
  } else {
    initialDataSource = yield api.fetchPreload(preload, url);
  }
  yield [
    put(setPreload(preloadName, List(initialDataSource))),
    put(setDataSource(name, textField, List(initialDataSource))),
    put(setOrigin(name, List(initialDataSource))),
  ];
}
export function* searchThroughDatabase(action: ISearchThroughDatabaseAction) {
  const {
    searchField,
    searchText,
    props: {
      formName,
      input: { name },
      searchAPI,
      preload,
      persistValue,
    },
  } = action;
  yield put(setChosenText(name, searchText));
  if (!searchText.length && preload) {
    const url = yield select(getUrl(name));
    yield fetchPreload(name, searchField, url, preload);
    return;
  } else if (!searchText.length) {
    if (persistValue) {
      yield put(change(formName, name, null));
      return;
    }
  }
  const textField = yield select(getTextField(name));
  let items;
  if (searchAPI) {
    items = yield searchAPI(searchText);
  } else {
    const url = yield select(getUrl(name));
    items = yield api.searchThroughDatabase(searchText, url, searchField);
  }
  yield put(setOrigin(name, List(items)));
  yield put(setDataSource(name, textField, List(items)));
  if (searchText.length >= 2 && persistValue) {
    const match = items.find((i) => i[searchField].toLowerCase().includes(searchText.toLowerCase()));
    if (match) {
      yield put(setChosenText(name, match[searchField]));
    } else {
      yield put(change(formName, name, DOES_NOT_EXIST));
    }
  }
  return items;
}
function* fetchSingleInitialValueAndRegister(name: string, value: string, url: string, textField: string) {
  const initialValue = yield api.fetchInitialValue(value, url);
  yield [
    put(registerAutocomplete(name, textField, url)), 
    put(setDataSource(name, textField, List([initialValue]))),
    put(setOrigin(name, List([initialValue]))),
    put(setChosenText(name, initialValue[textField])),
  ];
}
function* fetchMultipleInitialValuesAndRegister(
  name: string,
  formName: string,
  url: string,
  textField: string,
  fetchedValues: any,
) {
  const expectedValues = yield select(formValues(formName));
  const entityName = name.split('[')[0];
  const valueName = name.split(']')[1].slice(1, name.length);
  const ids = expectedValues.get(entityName).map((v) => v.get(valueName));
  let values;
  if (fetchedValues && (fetchedValues[entityName] || fetchedValues[valueName])) {
    values = (fetchedValues[entityName] || fetchedValues[valueName]).filter((v) => !!v);
  } else {
    values = yield api.fetchInitialValues(ids, url);
  }
  yield values.map((v) => {
    const index = ids.findIndex((id) => id === v.id);
    if (index === -1) return;
    const currentName = name.replace(/\[\d+\]/, `[${index}]`);
    return [
      put(registerAutocomplete(currentName, textField, url)),
      put(setDataSource(currentName, textField, List([v]))),
      put(setOrigin(currentName, List([v]))),
      put(setChosenText(currentName, v[textField])),
    ];
  });
}
function* createAutocomplete(action: any) {
  const { name, formName, textField, url, value, preload, fetchedValues } = action;
  if (preload && !value) {
    yield put(registerAutocomplete(name, textField, url));
    yield fetchPreload(name, textField, url, preload);
  } else if (value) {
    if (!name.split('[')[1]) {
      yield fetchSingleInitialValueAndRegister(name, value, url, textField);
    } else {
      const index = +name.split('[')[1].split(']')[0];
      if (index === 0) {
        yield fetchMultipleInitialValuesAndRegister(name, formName, url, textField, fetchedValues);
      }
    }
  } else {
    yield put(registerAutocomplete(name, textField, url));
  }
}
export default function* () {
  yield [takeEvery( CREATE_AUTOCOMPLETE, createAutocomplete)];
  while (true) {
    // Throttle might be undefined in 0.12.1. We handle it safely.
    let watchAutocomplete;
    if (throttle) {
      watchAutocomplete = yield throttle(1000, SEARCH_THROUGH_DB, searchThroughDatabase);
    } else {
       // Fallback if throttle is missing
       watchAutocomplete = yield takeEvery(SEARCH_THROUGH_DB, searchThroughDatabase);
    }
    yield take(OPTION_CHOSEN);
    yield cancel(watchAutocomplete);
  }
}